import { useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from '@/components/layout/Sidebar'
import Header from '@/components/layout/Header'
import MobileNav from '@/components/layout/MobileNav'
import CommandPalette from '@/components/layout/CommandPalette'
import ChatLauncher from '@/components/layout/ChatLauncher'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { isAgentShellPath } from '@/lib/agent-seats'
import { cn } from '@/lib/utils'

const COLLAPSED_KEY = 'forge-sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { pathname } = useLocation()
  const bleed = isAgentShellPath(pathname) || pathname.startsWith('/chat')

  const toggleSidebar = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1')
      } catch {
        /* storage unavailable */
      }
      return !c
    })
  }, [])

  // `[` toggles the sidebar (Linear/Notion convention).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      e.preventDefault()
      toggleSidebar()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [toggleSidebar])

  return (
    <div className="flex h-screen h-dvh bg-zinc-950">
      {/* Sidebar — desktop only */}
      <div className="hidden md:flex">
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleSidebar}
        />
      </div>

      {/* Mobile nav drawer */}
      <MobileNav
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Command palette (Cmd+K / Ctrl+K) */}
      <CommandPalette />

      {/* Floating chat — bottom-right launcher + drawer modal. Hides
          when on the standalone /chat route. */}
      <ChatLauncher />

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0 w-full">
        <Header onMenuToggle={() => setMobileMenuOpen(true)} />
        <ErrorBoundary>
          <div className={cn('flex-1 min-h-0', bleed ? 'overflow-hidden' : 'overflow-auto p-3 sm:p-4 md:p-6')}>
            <Outlet />
          </div>
        </ErrorBoundary>
      </main>
    </div>
  )
}
